import { useInView } from '@/hooks/useInView';

const steps = [
  {
    number: '01',
    title: 'Initial Call',
    subtitle: 'A friendly, no-pressure conversation',
    desc: 'It starts with a simple phone call. We listen to your situation, answer your questions honestly, and help you understand whether home care is the right fit. No jargon, no sales pitch.',
    detail: 'Typically 20 to 30 minutes. You can call us or we call you.',
  },
  {
    number: '02',
    title: 'Care Assessment',
    subtitle: 'We come to you, in your own home',
    desc: 'One of our senior team visits in person to understand the full picture: daily routines, health needs, personal preferences, and what matters most to the individual. From this, we build a personalised care plan together.',
    detail: 'Your care plan is reviewed regularly and updated as needs change.',
  },
  {
    number: '03',
    title: 'Care Begins',
    subtitle: 'Your dedicated carer, from day one',
    desc: 'We match you with a carer who fits your personality and needs. They follow your care plan, keep detailed notes, and communicate with your family as agreed. Care starts on your terms, at your pace.',
    detail: 'Same carer, every visit. Consistency you can count on.',
  },
];

function Step({ step, i }: { step: typeof steps[0]; i: number }) {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className="relative flex flex-col md:flex-row gap-6 md:gap-10 transition-all ease-out"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : 'translateY(20px)',
        transitionDelay: `${i * 120}ms`,
        transitionDuration: '700ms',
      }}
    >
      {/* Large background number */}
      <div className="shrink-0 w-full md:w-28 flex items-start">
        <span
          className="font-bold text-[#2C3E50]/6 select-none leading-none"
          style={{ fontSize: 'clamp(5rem, 10vw, 8rem)' }}
        >
          {step.number}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 pb-12 md:pb-16 border-b border-[#2C3E50]/10 last:border-0">
        <p className="text-[#C0706A] text-[10px] tracking-[0.25em] uppercase font-semibold mb-3">{step.subtitle}</p>
        <h3
          className="text-[#2C3E50] font-bold mb-4"
          style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.7rem)' }}
        >
          {step.title}
        </h3>
        <p className="text-[#2C3E50]/55 text-sm leading-relaxed mb-5 max-w-lg">{step.desc}</p>
        <p className="text-[#2C3E50]/35 text-xs italic">{step.detail}</p>
      </div>
    </div>
  );
}

export default function HowItWorks() {
  const { ref: headerRef, inView: headerInView } = useInView<HTMLDivElement>({ threshold: 0.2 });
  const { ref: ctaRef, inView: ctaInView } = useInView<HTMLDivElement>({ threshold: 0.3 });

  return (
    <section id="how-it-works" className="bg-[#FAFAF8] border-t border-[#2C3E50]/8">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">

        <div
          ref={headerRef}
          className="mb-14 md:mb-16 max-w-xl transition-all duration-700 ease-out"
          style={{ opacity: headerInView ? 1 : 0, transform: headerInView ? 'none' : 'translateY(16px)' }}
        >
          <p className="text-[#C0706A] text-[10px] tracking-[0.3em] uppercase font-semibold mb-5">
            How it works
          </p>
          <h2
            className="text-[#2C3E50] font-bold leading-[1.12]"
            style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)' }}
          >
            From first call to care that fits.
          </h2>
          <p className="text-[#2C3E50]/45 text-sm leading-relaxed mt-4">
            Getting started is straightforward. Here is exactly what to expect when you reach out to us.
          </p>
        </div>

        <div className="space-y-0">
          {steps.map((step, i) => (
            <Step key={step.number} step={step} i={i} />
          ))}
        </div>

        <div
          ref={ctaRef}
          className="mt-10 pt-10 border-t border-[#2C3E50]/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 transition-all duration-700 ease-out"
          style={{ opacity: ctaInView ? 1 : 0, transform: ctaInView ? 'none' : 'translateY(12px)' }}
        >
          <div>
            <p className="text-[#2C3E50] font-semibold text-base mb-1">Ready to take the first step?</p>
            <p className="text-[#2C3E50]/40 text-sm">Call us or send a message. We will get back to you the same day.</p>
          </div>
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#C0706A] hover:bg-[#a85f59] text-white font-bold px-8 py-3.5 text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap rounded-lg self-start sm:self-auto"
          >
            Book your free call
          </button>
        </div>

      </div>
    </section>
  );
}
